/**
 * useProgramModules
 *
 * The single registry + visibility resolver for the participant "Connect" surface
 * (the flat chip bar at the bottom of the timeline bar). It READS THROUGH the
 * existing per-program enablement sources (coaching flag + policy, journal flag,
 * peers/team activity presence) — no new data model — and returns the ordered list
 * of chips a given participant should see.
 *
 * Most chips open a dialog that MIRRORS the existing activity/component (Coach →
 * ParticipantCoachingDialog, Journal → JournalDialog, Peers → m-peers, Team →
 * m-team); the underlying activities are left untouched. Share + Leave are program
 * actions surfaced in the same flat chip language for one coherent cluster (Leave
 * is rendered as a muted-red "danger" chip).
 */
import { computed, type Ref, type ComputedRef, type Component } from 'vue';

export type ProgramModuleKey = 'coach' | 'journal' | 'team' | 'share' | 'leave' | 'places';

/** Rail taxonomy section a module belongs to (people-forward grouping). */
export type ProgramModuleSection = 'people' | 'support' | 'account';

export interface ProgramModule {
  key: ProgramModuleKey;
  label: string;
  /** mdi icon name. */
  icon: string;
  color?: string;
  /** Rail section this module lives under (PEOPLE / SUPPORT / ACCOUNT). */
  section: ProgramModuleSection;
  /** 'danger' renders a muted-red chip (e.g. Leave). */
  variant?: 'danger';
}

// ============================================================
// Program-Modules Framework (Wave 2 — behind PROGRAM_MODULES)
// ============================================================
//
// A PARALLEL descriptor layer over the thin registry above. The existing
// `ProgramModule` display metadata + `isVisible` switch are UNTOUCHED and keep
// driving the bar when PROGRAM_MODULES is off. The descriptor adds the optional
// capability hooks a full module (Places — next wave; Coaching — the wave after)
// can declare: an async panel, an onboarding phase contribution, a policy loader,
// and a nudge state-machine. The three generic consumers are (1) ProgramModuleBar's
// nudge-badge slot, (2) useAIPMOnboarding's phase-list builder, and (3) the shared
// trigger runner (useModuleTriggers).

/** Nudge state a module's cadence resolves to (drives the rail dot + auto-open). */
export type ModuleNudgeState = 'idle' | 'due' | 'overdue';

/**
 * The resolved per-program policy for a module (loaded by `policyLoader`).
 *
 * Shape mirrors CoachingPolicy: whether the module is enabled/required, its
 * rigidity (soft = dismissible nudge, hard = re-pops every load), and the
 * in-app nudge channel toggle. Open-ended (`[k: string]: unknown`) so a module
 * can fold its own history/cadence fields (e.g. sessions, schedule windows) into
 * the same object its `deriveState` reads.
 */
export interface ModulePolicy {
  enabled: boolean;
  required?: boolean;
  rigidity?: 'soft' | 'hard';
  inAppNudge?: boolean;
  [k: string]: unknown;
}

/**
 * A module's contributed onboarding phase (concatenated into BASE_PHASES by
 * useAIPMOnboarding's builder at the position coaching occupies today).
 */
export interface ModuleOnboardingPhase {
  /** Async component rendered for this phase inside the onboarding dialog. */
  component: () => Promise<Component>;
  /** Whether this phase should appear for the given onboarding context. */
  shouldShow: (ctx: unknown) => boolean;
  /** Optional gate: can the participant advance past this phase? */
  canAdvance?: (state: unknown) => boolean;
}

/**
 * ProgramModuleDescriptor — the existing registry entry PLUS optional capability
 * hooks. The existing 5 keys register as THIN WRAPS (display metadata only, no
 * hooks) — their behavior is dispatched by the host's `onModuleSelect` shim
 * exactly as today. Full modules (Places, Coaching) supply the hooks.
 */
export interface ProgramModuleDescriptor extends ProgramModule {
  /** Async panel component. Thin wraps omit this — they route via the dispatch shim. */
  panel?: () => Promise<Component>;
  /** Onboarding phase contribution (fed to useAIPMOnboarding's phase-list builder). */
  onboardingPhase?: ModuleOnboardingPhase;
  /** Loads the per-program ModulePolicy (drives the trigger runner). */
  policyLoader?: (programId: string) => Promise<ModulePolicy>;
  /** Cadence state-machine: policy (+ optional history) → idle|due|overdue. */
  nudge?: {
    deriveState: (policy: ModulePolicy, history: unknown) => ModuleNudgeState;
  };
}

/** The runtime signals that decide which chips a participant sees. */
export interface ProgramModuleContext {
  /** Current viewer is a participant (not organizer/template). */
  isParticipant: boolean;
  /** Checked out by a real sponsor (not 'Unsponsored' / unset). */
  isSponsored: boolean;
  /** ENABLE_COACHING flag. */
  coachingEnabled: boolean;
  /** Journal feature gate (as TimelineBar computes it today). */
  journalEnabled: boolean;
  /** Team is enabled/joinable for this program. */
  teamAvailable?: boolean;
  /**
   * Places module is available for this program (behind PROGRAM_MODULES + PLACES_V1):
   * the program references at least one in-person Place / deliveryModality != online.
   * The host computes this (a places-exist check) since the registry has no data model.
   * Undefined/false → the Places chip is hidden → byte-identical to pre-Places DOM.
   */
  placesAvailable?: boolean;
}

/** Display metadata. Visibility is decided separately. */
const REGISTRY: Record<ProgramModuleKey, ProgramModule> = {
  // PEOPLE — social/growth surfaces
  team: {
    key: 'team',
    label: 'Team',
    icon: 'mdi-account-group',
    color: 'primary',
    section: 'people'
  },
  coach: {
    key: 'coach',
    label: 'Coach',
    icon: 'mdi-account-voice',
    color: 'primary',
    section: 'people'
  },
  journal: {
    key: 'journal',
    label: 'Journal',
    icon: 'mdi-book-open-variant',
    color: 'primary',
    section: 'people'
  },
  // Places — in-person facilitation (where the program happens + check-in/out).
  places: {
    key: 'places',
    label: 'Places',
    icon: 'mdi-map-marker',
    color: 'primary',
    section: 'people'
  },
  // SUPPORT — utility actions
  share: {
    key: 'share',
    label: 'Share',
    icon: 'mdi-share-variant-outline',
    color: 'primary',
    section: 'support'
  },
  // ACCOUNT — destructive, confirm-gated (muted-red)
  leave: {
    key: 'leave',
    label: 'Leave',
    icon: 'mdi-location-exit',
    variant: 'danger',
    section: 'account'
  }
};

// People-forward taxonomy: PEOPLE (team, coach, journal).
// NOTE: `share` + `leave` are intentionally NOT in the rail — they're consolidated into the
// participant "Settings" panel (alongside Preferences). The rail renders the rest grouped by section.
const ORDER: ProgramModuleKey[] = ['team', 'coach', 'journal', 'places'];

function isVisible(key: ProgramModuleKey, ctx: ProgramModuleContext): boolean {
  if (!ctx.isParticipant) return false;
  switch (key) {
    case 'coach':
      return ctx.coachingEnabled && ctx.isSponsored;
    case 'journal':
      return ctx.journalEnabled && ctx.isSponsored;
    case 'team':
      return !!ctx.teamAvailable && ctx.isSponsored;
    case 'places':
      // In-person facilitation. Visible only when the host flags the program as
      // having places (behind PROGRAM_MODULES + PLACES_V1). Not gated on sponsorship:
      // knowing WHERE to go is foundational, available to any participant.
      return !!ctx.placesAvailable;
    case 'share':
      // Invite stakeholders to monitor progress — available to any participant.
      return true;
    case 'leave':
      // Always available to a participant (muted-red, confirm-gated).
      return true;
    default:
      return false;
  }
}

export function useProgramModules(
  ctx: Ref<ProgramModuleContext> | ComputedRef<ProgramModuleContext>
) {
  const visibleModules = computed<ProgramModule[]>(() =>
    ORDER.filter(k => isVisible(k, ctx.value)).map(k => REGISTRY[k])
  );

  // Section-grouped views for the codified rail taxonomy (PEOPLE / SUPPORT / ACCOUNT).
  const peopleModules = computed(() => visibleModules.value.filter(m => m.section === 'people'));
  const supportModules = computed(() => visibleModules.value.filter(m => m.section === 'support'));
  const accountModules = computed(() => visibleModules.value.filter(m => m.section === 'account'));

  return { visibleModules, peopleModules, supportModules, accountModules };
}

// ============================================================
// Descriptor registry (Wave 2 — thin wraps of the existing 5 keys)
// ============================================================
//
// Each existing key is registered as a THIN WRAP: the descriptor carries ONLY the
// existing display metadata (spread from REGISTRY) — no panel, onboardingPhase,
// policyLoader, or nudge. Selecting one still routes through the host's existing
// `onModuleSelect` switch (the DISPATCH SHIM, documented below), so behavior for
// coach/journal/team/share/leave is identical whether PROGRAM_MODULES is on or off.
//
// DISPATCH SHIM (documented contract):
//   The bar emits `select(key)`; the host (TimelineBar.onModuleSelect) maps the key
//   to its existing handler — coach→openCoachingPanel, journal→openJournalDialog,
//   team→teamDialog, share→openShareAccess, leave→sureLeaveProgram. A thin wrap
//   declares NO `panel`, so the framework hands the key back to this same shim. A
//   FULL module (Places, next wave) declares a `panel`; the host mounts it directly.
//   Either path is byte-identical for the existing 5 keys — they never gain a panel.
const MODULE_DESCRIPTORS: Record<ProgramModuleKey, ProgramModuleDescriptor> = {
  team: { ...REGISTRY.team },
  coach: { ...REGISTRY.coach },
  journal: { ...REGISTRY.journal },
  // Thin wrap by default; usePlaces().registerDescriptor() upgrades it to a FULL
  // module (panel + policyLoader + nudge + onboardingPhase) behind PROGRAM_MODULES.
  places: { ...REGISTRY.places },
  share: { ...REGISTRY.share },
  leave: { ...REGISTRY.leave }
};

/** All registered module descriptors, keyed. Additive — used by the framework consumers. */
export function getModuleDescriptors(): Record<ProgramModuleKey, ProgramModuleDescriptor> {
  return MODULE_DESCRIPTORS;
}

/** The descriptor for a single key (thin wrap or full module). */
export function getModuleDescriptor(key: ProgramModuleKey): ProgramModuleDescriptor {
  return MODULE_DESCRIPTORS[key];
}

/**
 * Register (or replace) a module descriptor — the seam a full module (Places,
 * Coaching) uses to attach its panel/policyLoader/nudge/onboardingPhase hooks to
 * an existing key WITHOUT touching the thin-wrap defaults. Additive-only: called
 * from the module's own setup, behind PROGRAM_MODULES, in a later wave.
 */
export function registerModuleDescriptor(
  key: ProgramModuleKey,
  patch: Partial<Omit<ProgramModuleDescriptor, 'key'>>
): void {
  MODULE_DESCRIPTORS[key] = { ...MODULE_DESCRIPTORS[key], ...patch, key };
}

/** Descriptors that declare a policyLoader — the set the trigger runner iterates. */
export function getPolicyDrivenDescriptors(): ProgramModuleDescriptor[] {
  return (Object.keys(MODULE_DESCRIPTORS) as ProgramModuleKey[])
    .map(k => MODULE_DESCRIPTORS[k])
    .filter(d => typeof d.policyLoader === 'function' && !!d.nudge);
}
